import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { PagingViewModel } from 'autogen/swagger/all';
import { Last, Next, OutOf } from 'autogen/translation-keys/trans-website-paging';
import { ButtonWithIcon } from 'components/Button';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import { breakpoint, sizes } from 'variables';
import PagingSelect from './components/PagingSelect';
import usePagination, { type PaginationCallback } from './shared/hooks/usePagination';

type PaginationProps = {
    model: PagingViewModel;
    onPaginate?: PaginationCallback;
    layout?: 'inline' | 'stacked';
};

/* #region styles */
const Nav = styled.nav`
    ${breakpoint.up('768px')} {
        width: fit-content;
        margin: 0 auto;
    } 
`;

const Button = styled(ButtonWithIcon)`
    width: 100%;
`;

function getLayoutStyling(layout: PaginationProps['layout']) {
    switch (layout) {
        case 'stacked':
            return css`
                grid-template-areas:
                'select select'
                'previous next';
                `;
        case 'inline':
        default:
            return css`
                grid-template-areas:
                    'select select'
                    'previous next';

                ${breakpoint.up(sizes.sm)} {
                    grid-template-areas: 'previous select next';
                    column-gap: ${({ theme }) => theme.Spacing24};
                }
            `;
    }
}

const PaginationContainer = styled.ul<Pick<PaginationProps, 'layout'>>`
    display: grid;
    column-gap: ${({ theme }) => theme.Spacing8};
    row-gap: ${({ theme }) => theme.Spacing16};
    list-style: none;
    margin-block: 0;
    padding-inline: 0;
    ${({ layout }) => getLayoutStyling(layout)}
`;
const PaginationSelectContainer = styled.li`
    grid-area: select;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.Spacing8};
    justify-self: center;
`;
const PaginationButtonContainer = styled.li``;

const PaginationPrev = styled(PaginationButtonContainer)`
    grid-area: previous;
`;

const PaginationNext = styled(PaginationButtonContainer)`
    grid-area: next;
`;
/* #endregion */

export default function Pagination(props: PaginationProps) {
    const { model, onPaginate, layout } = props;
    const { pages, numberOfPages } = model;
    const { t } = useTranslation();

    const { changePage, nextPage, previousPage, hasNextPage, hasPreviousPage, currentPageNumber } = usePagination(
        model,
        model.queryStringName,
        onPaginate
    );

    const pagesNumbers = useMemo(() => pages.map((page) => page.pageNumber), [pages]);

    if (pages.length <= 1) return null;

    return (
        <Nav>
            <PaginationContainer layout={layout}>
                <PaginationPrev>
                    <Button
                        variant="tertiary"
                        size="lg"
                        icon={<Icon type="ChevronLeft" size={16} />}
                        iconProperties={{ position: 'left' }}
                        disabled={!hasPreviousPage}
                        onClick={previousPage}
                    >
                        {t(Last)}
                    </Button>
                </PaginationPrev>
                <PaginationSelectContainer>
                    <PagingSelect
                        currentPageNumber={currentPageNumber}
                        pages={pagesNumbers}
                        numberOfPages={numberOfPages}
                        onChange={changePage}
                    />
                    <Typography.Body size="M" strong as="span">
                        {t(OutOf)} {numberOfPages}
                    </Typography.Body>
                </PaginationSelectContainer>
                <PaginationNext>
                    <Button
                        variant="tertiary"
                        size="lg"
                        icon={<Icon type="ChevronRight" size={16} />}
                        iconProperties={{ position: 'right' }}
                        disabled={!hasNextPage}
                        onClick={nextPage}
                    >
                        {t(Next)}
                    </Button>
                </PaginationNext>
            </PaginationContainer>
        </Nav>
    );
}
